import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { supabase } from "../lib/supabase";

interface CompanyContextType {
  selectedCompany: number | null;
  companyDetails?: {
    id?: number;
    status: string;
    name?: string;
    street?: string;
    house_number?: string;
    moneybird_contact_id?: string;
    invoice_email?: string;
    pricing_payment_term?: number;
  };
  companyDetailsLoading: boolean;
  setSelectedCompany: (id: number) => void;
  refetchCompanyDetails: () => Promise<void>;
}

export const CompanyContext = createContext<CompanyContextType | undefined>(
  undefined
);

export const CompanyProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedCompany, setSelectedCompany] = useState<number | null>(() => {
    const saved = localStorage.getItem("selectedCompany");
    return saved ? parseInt(saved) : null;
  });

  const [companyDetails, setCompanyDetails] = useState<{
    id?: number;
    status: string;
    name?: string;
    street?: string;
    house_number?: string;
    moneybird_contact_id?: string;
    invoice_email?: string;
    pricing_payment_term?: number;
  }>({
    status: "active",
    name: "Your Company",
    street: "",
    house_number: "",
    moneybird_contact_id: undefined,
    invoice_email: undefined,
    pricing_payment_term: undefined,
  });

  const [companyDetailsLoading, setCompanyDetailsLoading] = useState(false);

  // Memoize fetch company details function to prevent unnecessary recreations
  const fetchCompanyDetails = useCallback(async (companyId: number) => {
    setCompanyDetailsLoading(true);
    try {
      const { data, error } = await supabase
        .from("company")
        .select(
          "id, name, status, street, house_number, moneybird_contact_id, invoice_email, pricing_payment_term"
        )
        .eq("id", companyId)
        .single();

      if (error) {
        console.error("Error fetching company details:", error);

        // Handle specific error cases
        if (
          error.code === "PGRST116" ||
          error.message?.includes(
            "JSON object requested, multiple (or no) rows returned"
          )
        ) {
          console.warn(
            `Company with ID ${companyId} not found. Using default company details.`
          );
          // Company not found - could be deleted or user doesn't have access
          localStorage.removeItem("selectedCompany");
          setSelectedCompany(null);
        } else if (error.code === "42P01") {
          // PostgreSQL code for "relation does not exist"
          console.log(
            "Company table does not exist yet, using default company name"
          );
        }

        setCompanyDetails({
          status: "active",
          name: "Your Company",
          street: "",
          house_number: "",
          moneybird_contact_id: undefined,
          invoice_email: undefined,
          pricing_payment_term: undefined,
        });
        return;
      }

      if (data) {
        setCompanyDetails({
          id: data.id,
          status: data.status || "active",
          name: data.name || "Your Company",
          street: data.street || "",
          house_number: data.house_number || "",
          moneybird_contact_id: data.moneybird_contact_id || undefined,
          invoice_email: data.invoice_email || undefined,
          pricing_payment_term: data.pricing_payment_term || undefined,
        });
      } else {
        // Fallback if no data is returned
        setCompanyDetails({
          status: "active",
          name: "Your Company",
          street: "",
          house_number: "",
          moneybird_contact_id: undefined,
          invoice_email: undefined,
          pricing_payment_term: undefined,
        });
      }
    } catch (error) {
      console.error("Error in company details fetch:", error);
      setCompanyDetails({
        status: "active",
        name: "Your Company",
        street: "",
        house_number: "",
        moneybird_contact_id: undefined,
        invoice_email: undefined,
        pricing_payment_term: undefined,
      });
    } finally {
      setCompanyDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyDetails(selectedCompany);
    } else {
      // Set default company details if no company is selected
      setCompanyDetails({
        status: "active",
        name: "Your Company",
        street: "",
        house_number: "",
        moneybird_contact_id: undefined,
        invoice_email: undefined,
        pricing_payment_term: undefined,
      });
    }
  }, [selectedCompany, fetchCompanyDetails]);

  const handleCompanyChange = useCallback((id: number) => {
    setSelectedCompany(id);
    localStorage.setItem("selectedCompany", id.toString());
  }, []);

  // Add a refetch function that can be called from components
  const refetchCompanyDetails = useCallback(async () => {
    if (selectedCompany) {
      await fetchCompanyDetails(selectedCompany);
    }
  }, [selectedCompany, fetchCompanyDetails]);

  // Update the context value to include the refetch function
  const contextValue = useMemo(
    () => ({
      selectedCompany,
      companyDetails,
      companyDetailsLoading,
      setSelectedCompany: handleCompanyChange,
      refetchCompanyDetails,
    }),
    [
      selectedCompany,
      companyDetails,
      companyDetailsLoading,
      handleCompanyChange,
      refetchCompanyDetails,
    ]
  );

  return (
    <CompanyContext.Provider value={contextValue}>
      {children}
    </CompanyContext.Provider>
  );
};
